import { Dispatch } from "redux"
import { ValidationError } from "sequelize"
import {
    MtDriveFolder,
    MtDriveFolderCreate,
    MtDriveFile,
} from "server/models"
import { OrderType } from "client/utils"
import { GET, POST, validate, sort } from "client/store/utils"
import {
    NestedFolder,
    UserData,
    LangType,
    FolderValidation,
    ProjectUserList,

    LoadProjectUsers,
    LoadProjectUserGroups,
    LoadProjectPermissions,
    LoadAllDriveFiles,
    DeleteDriveFiles,

    setProjectUserList,
    setSystemProjectUserGroups,
    setSystemProjectPermissions,

    createFolderName,
    createRecycleFolderName,
    createFileName,
    createRecycleFileName,
} from "client/store"

export const LoadAllDriveFolders = (accountId: number) => (
    GET(`/mtDriveFolder/all/accountId/${accountId}`)
    .then((folders: MtDriveFolder[]) => folders)
)

export const LoadDriveFolders = (
    accountId: number,
    parentFolderId: number,
    sortColumn?: keyof MtDriveFolder,
    sortOrder?: OrderType,
) => (
    GET(`/mtDriveFolder/accountId/${accountId}/folderId/${parentFolderId}`)
    .then((folders: MtDriveFolder[]) => {
        if (sortColumn && sortOrder) {
            folders.sort((a, b) => {
                const A: MtDriveFolder = { ...a, name: createFolderName(a) }
                const B: MtDriveFolder = { ...b, name: createFolderName(b) }
                return sort(A, B, sortColumn, sortOrder)
            })
        }
        return folders
    })
)

export const LoadDriveFolderById = (folderId: number) => (
    GET(`/mtDriveFolder/id/${folderId}`)
    .then((folder: MtDriveFolder) => folder || null)
)

export const LoadDriveFolderByProjectId = (projectId: number) => (
    GET(`/mtDriveFolder/projectId/${projectId}`)
    .then((folder: MtDriveFolder) => folder || null)
)

export const LoadDriveFolderByQidProductId = (qidProductId: number) => (
    GET(`/mtDriveFolder/qidProductId/${qidProductId}`)
    .then((folder: MtDriveFolder) => folder || null)
)

export const LoadProjectFolderAccessData = (
    projectId: number,
    userData: UserData,
    dispatch: Dispatch,
) => (
    Promise.all([
        LoadProjectUsers(projectId).then((res) => {
            const projectUserList: ProjectUserList = { [projectId]: res }
            dispatch(setProjectUserList(projectUserList))
        }),
        LoadProjectUserGroups(userData.userId, userData.accountId, projectId)
        .then((projectGroupIds) => {
            dispatch(setSystemProjectUserGroups(projectId, projectGroupIds))
        }),
        LoadProjectPermissions(userData.accountId, projectId)
        .then((permissions) => {
            dispatch(setSystemProjectPermissions(projectId, permissions.permissions))
        }),
    ])
)

export const CreateRootFolder = (lang: LangType) => {
    const rootFolder: MtDriveFolder = {
        id: 0,
        folderId: undefined,
        prevFolderId: undefined,
        accountId: undefined,
        projectId: undefined,
        qidProductId: undefined,
        name: lang.drive,
        copy: null,
    }
    return rootFolder
}

export const CreateRecycleFolder = (lang: LangType) => {
    const recycle: MtDriveFolder = {
        id: null,
        folderId: undefined,
        prevFolderId: undefined,
        accountId: undefined,
        projectId: undefined,
        qidProductId: undefined,
        name: lang.recycle,
        copy: null,
    }
    return recycle
}

export const LoadNestedFolders = (
    accountId: number,
    targetFolder: MtDriveFolder,
    lang: LangType,
) => {
    const root: NestedFolder = {
        folder: CreateRootFolder(lang),
        folders: {},
        files: {},
    }

    root.folders[lang.recycle] = {
        folder: CreateRecycleFolder(lang),
        folders: {},
        files: {},
    }

    return LoadAllDriveFolders(accountId).then((folders) => (
        LoadAllDriveFiles(accountId).then((files) => {
            // prepare associative folders
            const folderList: { [folderId: number]: NestedFolder } = {}
            folders.forEach((folder) => {
                folderList[folder.id] = {
                    folder,
                    folders: {},
                    files: {},
                }
            })

            // add files to folders
            files.forEach((file) => {
                if (file.folderId === 0) { // in the root folder
                    const index = createFileName(file)
                    root.files[index] = file
                } else if (file.folderId === null) { // in the root of recycle folder
                    const recycleIndex = createRecycleFileName(file)
                    root.folders[lang.recycle].files[recycleIndex] = file
                } else {
                    const index = createFileName(file)
                    folderList[file.folderId].files[index] = file
                }
            })

            // prepare nested folders
            folders.forEach((folder) => {
                const nestedFolder = folderList[folder.id]

                if (folder.folderId === 0) { // in the root folder
                    const index = createFolderName(folder)
                    root.folders[index] = nestedFolder
                } else if (folder.folderId === null) { // in the root of recycle folder
                    const recycleIndex = createRecycleFolderName(folder)
                    root.folders[lang.recycle].folders[recycleIndex] = nestedFolder
                } else {
                    const index = createFolderName(folder)
                    folderList[folder.folderId].folders[index] = nestedFolder
                }
            })

            // target folder for search or remove
            if (targetFolder && targetFolder.id) return folderList[targetFolder.id]
            if (targetFolder && targetFolder.id === null) return root.folders[lang.recycle]
            if (targetFolder && targetFolder.id === 0) return root

            return root
        })
    ))
}

export const CreateDriveFolder = (
    folder: MtDriveFolderCreate,
    lang: LangType,
) => (
    POST(
        "/mtDriveFolder/create",
        JSON.stringify(folder),
    ).then((res) => {
        const newFolder = res as MtDriveFolder
        const error = res as ValidationError
        return {
            newFolder: newFolder.id ? newFolder : null,
            validation: <FolderValidation>validate(error, lang),
        }
    })
)

export const RenameDriveFolder = (
    folder: MtDriveFolder,
    lang: LangType,
) => (
    POST(
        "/mtDriveFolder/rename",
        JSON.stringify(folder),
    ).then((res) => {
        const error = res as ValidationError
        return {
            validation: <FolderValidation>validate(error, lang),
        }
    })
)

export const MoveDriveFoldersToRecycle = (folders: readonly MtDriveFolder[]) => {
    const removedFolders: MtDriveFolder[] = []
    folders.forEach((folder) => {
        removedFolders.push({
            ...folder,
            prevFolderId: folder.folderId,
            folderId: null,
        })
    })

    return POST(
        "/mtDriveFolder/moveToRecycle",
        JSON.stringify(removedFolders),
    )
}

export const MoveDriveFolder = (folder: MtDriveFolder) => (
    POST(
        "/mtDriveFolder/move",
        JSON.stringify(folder),
    )
)

export const DeleteDriveFolders = (folderIds: number[]) => (
    POST(
        "/mtDriveFolder/delete",
        JSON.stringify(folderIds),
    ).then((removedRows: number) => removedRows)
)

export const DeleteDriveFolderIrreversibly = (
    targetFolder: MtDriveFolder,
    removeTargetFolder: boolean,
    userData: UserData,
    lang: LangType,
) => (
    LoadNestedFolders(userData.accountId, targetFolder, lang)
    .then((nestedFolders) => {
        const foldersToDelete: number[] = []
        const filesToDelete: MtDriveFile[] = []

        if (removeTargetFolder) foldersToDelete.push(targetFolder.id)

        const deleteFiles = (files: MtDriveFile[]) => {
            files.forEach((file) => {
                filesToDelete.push(file)
            })
        }

        const deleteFolders = (folders: NestedFolder[]) => {
            folders.forEach((nestedFolder) => {
                foldersToDelete.push(nestedFolder.folder.id)

                deleteFiles(Object.values(nestedFolder.files))
                deleteFolders(Object.values(nestedFolder.folders))
            })
        }

        deleteFiles(Object.values(nestedFolders.files))
        deleteFolders(Object.values(nestedFolders.folders))

        return Promise.all([
            DeleteDriveFolders(foldersToDelete),
            DeleteDriveFiles(filesToDelete),
        ])
    })
)
